import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  UserRound,
  User,
  CheckCircle2,
  Clock,
  ArrowRight,
  Pencil,
  Upload,
  Image as ImageIcon,
  Mail,
  Building2,
  Globe,
  Layers,
  Tag,
  Info,
} from "lucide-react";

import { useCurrentUser } from "../../hooks/useCurrentUser";
import { useProfileData, getProfileCompletion } from "../../hooks/useProfileData";
import { uploadProfilePhoto } from "../../service/userService";
import { resolvePhotoUrl } from "../../utils/profilePhoto";
import "../../assets/css/ProfileModule.css";

const ProfileCompletionRing = ({ percent }) => {
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - ((percent ?? 0) / 100) * circumference;

  return (
    <div className="pm-ring-wrap">
      <svg width="100%" height="100%" viewBox="0 0 130 130" preserveAspectRatio="xMidYMid meet">
        <circle cx="65" cy="65" r={radius} fill="none" stroke="#EFEFEF" strokeWidth="10" />
        {percent !== null && (
          <circle
            cx="65"
            cy="65"
            r={radius}
            fill="none"
            stroke="#C72430"
            strokeWidth="10"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            transform="rotate(-90 65 65)"
            style={{ transition: "stroke-dashoffset 0.4s ease" }}
          />
        )}
      </svg>
      <div className="pm-ring-label">
        <div className="pm-ring-percent">{percent === null ? "—" : `${percent}%`}</div>
        <div className="pm-ring-caption">{percent === null ? "Loading" : "Complete"}</div>
      </div>
    </div>
  );
};

const InfoRow = ({ icon: Icon, label, value, action }) => (
  <div className="pm-info-row d-flex align-items-start justify-content-between gap-2">
    <div className="d-flex align-items-start gap-2">
      <div className="pm-info-icon-badge">
        <Icon size={14} />
      </div>
      <div>
        <div className="pm-info-label">{label}</div>
        <div className={`pm-info-value ${!value ? "pm-info-empty" : ""}`}>
          {value || "Not added yet"}
        </div>
      </div>
    </div>
    {action}
  </div>
);

const Profile = () => {
  const navigate = useNavigate();
  const { firstName, lastName, user } = useCurrentUser();
  const { profile, updateProfile, apiCompletion, apiLoading, refetch } = useProfileData({
    firstName,
    lastName,
    email: user?.personal_email || user?.email || user?.useremail,
  });
  const fileInputRef = useRef(null);
  const uploadInProgress = useRef(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState("");
  const [photoError, setPhotoError] = useState("");
  const [photoSuccess, setPhotoSuccess] = useState("");

  useEffect(() => () => {
    if (photoPreview) URL.revokeObjectURL(photoPreview);
  }, [photoPreview]);

  const { checklist, percent } = getProfileCompletion(apiCompletion);
  const hasCompletionData = percent !== null;
  const completedItems = checklist.filter((item) => item.complete);
  const pendingItems = checklist.filter((item) => !item.complete);

  const fullName = [profile.firstName, profile.lastName].filter(Boolean).join(" ");

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || uploadInProgress.current) return;
    e.target.value = "";
    setPhotoError("");
    setPhotoSuccess("");
    if (!profile.memberId) {
      setPhotoError("Your profile hasn't loaded. Please refresh the page and try again.");
      return;
    }
    if (!["image/jpeg", "image/png", "image/gif"].includes(file.type)) {
      setPhotoError("Choose a JPG, PNG or GIF image.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setPhotoError("Choose an image smaller than 5MB.");
      return;
    }
    uploadInProgress.current = true;
    setPhotoUploading(true);
    // Keep the temporary preview out of the persisted profile draft.
    setPhotoPreview(URL.createObjectURL(file));

    try {
      const response = await uploadProfilePhoto(file, profile.memberId);
      const savedPhoto = response.data?.profile_photo || response.data?.profile_photo_url;
      if (savedPhoto) updateProfile({ avatar: resolvePhotoUrl(savedPhoto, "") });
      await refetch();
      setPhotoSuccess("Profile photo uploaded successfully.");
    } catch (err) {
      setPhotoError(err.code === "ECONNABORTED" || err.code === "ETIMEDOUT"
        ? "The image is taking longer than expected to upload. Please check your connection and try again."
        : err.response?.data?.message || err.message || "Couldn't upload your photo. Please try again.");
    } finally {
      setPhotoPreview("");
      setPhotoUploading(false);
      uploadInProgress.current = false;
    }
  };

  const goToSetup = () => navigate("/settings/profile/setup");

  return (
    <div className="dashboard-search-results profile-module">
      <div className="row g-3">
        <div className="col-12">
          <div className="sr-shell-card p-3 p-md-4 d-flex align-items-center gap-3">
            <div className="pm-header-icon">
              <UserRound size={20} />
            </div>
            <div>
              <h1 className="sr-page-title">Profile</h1>
              <p className="sr-page-subtitle mb-0">View and manage your personal information</p>
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="sr-shell-card p-3 p-md-4">
            <h2 className="pm-card-title">Complete Your Profile</h2>
            <p className="pm-card-subtitle">
              Add more details to get more survey invitations and earn more coins.
            </p>

            <div className="pm-progress-block d-flex flex-column flex-md-row align-items-center align-items-md-stretch gap-4 mt-3">
              <ProfileCompletionRing percent={percent} />

              <div className="pm-progress-divider d-none d-md-block" />

              <div className="flex-grow-1 w-100">
                <div className="pm-progress-heading">
                  {!hasCompletionData
                    ? apiLoading
                      ? "Loading your profile..."
                      : "Profile data unavailable"
                    : percent >= 100
                    ? "Your profile is complete!"
                    : "You're just getting started!"}
                </div>
                <div className="pm-progress-subheading mb-3">
                  Complete your profile to unlock more features and earn rewards.
                </div>

                {hasCompletionData && (
                  <div className="row g-3">
                    <div className="col-6">
                      <div className="pm-checklist-title pm-completed-title">Completed</div>
                      <ul className="pm-checklist">
                        {completedItems.length === 0 && (
                          <li className="pm-empty">Nothing completed yet</li>
                        )}
                        {completedItems.map((item) => (
                          <li key={item.key}>
                            <CheckCircle2 size={14} /> {item.label}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="col-6">
                      <div className="pm-checklist-title pm-pending-title">Pending</div>
                      <ul className="pm-checklist pm-checklist-pending">
                        {pendingItems.length === 0 && <li className="pm-empty">All done!</li>}
                        {pendingItems.map((item) => (
                          <li key={item.key}>
                            <Clock size={14} /> {item.label}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="pm-progress-footer d-flex align-items-center justify-content-between flex-wrap gap-3 mt-4 pt-3">
              <div className="pm-footer-note d-flex align-items-start gap-2">
                <Info size={14} />
                <span>A complete profile helps others find you and increases your survey opportunities.</span>
              </div>
              {(!hasCompletionData || percent < 100) && (
                <button type="button" className="pm-primary-btn" onClick={goToSetup}>
                  Continue Profile Setup <ArrowRight size={16} />
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="sr-shell-card p-3 p-md-4">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h2 className="pm-card-title mb-0">Basic Information</h2>
              <button type="button" className="pm-outline-btn" onClick={goToSetup}>
                <Pencil size={14} /> Edit basic info
              </button>
            </div>

            <div className="pm-photo-box d-flex align-items-center gap-3">
              <div className="pm-photo-preview">
                {photoPreview || profile.avatar ? <img src={photoPreview || profile.avatar} alt="Profile" /> : <ImageIcon size={22} />}
              </div>
              <div className="flex-grow-1">
                <div className="pm-photo-title">
                  {profile.avatar ? "Profile Photo" : "No Profile Photo"}
                </div>
                <div className="pm-photo-subtitle">Add a photo to help others recognize you</div>
                <button
                  type="button"
                  className="pm-outline-btn mt-2"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={photoUploading || apiLoading}
                  aria-busy={photoUploading}
                >
                  <Upload size={14} /> {photoUploading ? "Uploading..." : profile.avatar ? "Change Photo" : "Upload Photo"}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif"
                  disabled={photoUploading || apiLoading}
                  className="d-none"
                  onChange={handlePhotoChange}
                />
                <div className="pm-photo-hint mt-2">JPG, PNG or GIF. Max size 5MB.</div>
                {photoError && <div className="pm-photo-hint text-danger mt-2" role="alert">{photoError}</div>}
                {photoSuccess && <div className="pm-photo-hint text-success mt-2" role="status">{photoSuccess}</div>}
              </div>
            </div>

            <div className="row g-3 mt-1">
              <div className="col-md-6">
                <InfoRow icon={User} label="Full Name" value={fullName} />
              </div>
              <div className="col-md-6">
                <InfoRow
                  icon={Mail}
                  label="Email Address"
                  value={profile.personalEmail}
                  action={
                    profile.personalEmail && profile.emailVerified ? (
                      <span className="pm-verified-badge">Verified</span>
                    ) : null
                  }
                />
              </div>
              <div className="col-md-6">
                <InfoRow icon={Building2} label="Company" value={profile.companyName} />
              </div>
              <div className="col-md-6">
                <InfoRow icon={Globe} label="Country" value={profile.country} />
              </div>
              <div className="col-md-6">
                <InfoRow icon={Layers} label="Sector" value={profile.sector} />
              </div>
              <div className="col-md-6">
                <InfoRow icon={Tag} label="Industry" value={profile.industry} />
              </div>
            </div>

            <div className="pm-note d-flex align-items-center gap-2 mt-3">
              <Info size={14} /> Keep your basic information up to date. This helps us personalize
              your survey experience.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
